import { useState } from 'react'
import { Alert, Button, Card, Tag, Typography } from 'antd'
import { CheckCircleOutlined, ClockCircleOutlined, StopOutlined } from '@ant-design/icons'
import type { DashboardWidgetType, PendingScheduleConfirmation, ScheduleImpactSchedule } from '../types'
import { VIZ_SWITCHER } from '../vizSwitcher'

type Props = {
  confirmation: PendingScheduleConfirmation
  resolving: boolean
  // False hides "Apply changes"; the user can still cancel.
  canAccept?: boolean
  onResolve: (accept: boolean) => void
}

const EFFECT_TAGS: Record<ScheduleImpactSchedule['effect'], { label: string; color: string }> = {
  continues: { label: 'Will continue', color: 'success' },
  widgetRemoved: { label: 'Widget removed', color: 'warning' },
  deleted: { label: 'Will be deleted', color: 'error' },
}

const RESOLVED_STATUS = {
  accepted: { icon: <CheckCircleOutlined />, label: 'Changes applied' },
  declined: { icon: <StopOutlined />, label: 'Cancelled' },
  stale: { icon: <ClockCircleOutlined />, label: 'Expired — the dashboard changed' },
} as const

function typeLabel(type?: DashboardWidgetType) {
  return VIZ_SWITCHER.find((entry) => entry.key === type)?.label ?? type ?? ''
}

export function ScheduleImpactConfirmCard({ confirmation, resolving, canAccept = true, onResolve }: Props) {
  const [action, setAction] = useState<'accept' | 'cancel' | null>(null)

  if (confirmation.status !== 'pending') {
    const status = RESOLVED_STATUS[confirmation.status]
    return (
      <Typography.Text type="secondary" className="text-xs">
        {status.icon} {status.label}
      </Typography.Text>
    )
  }

  const isReplace = confirmation.kind === 'replace'
  const hasRetype = confirmation.widgets.some((widget) => widget.change === 'retyped')
  const resolve = (accept: boolean) => {
    setAction(accept ? 'accept' : 'cancel')
    onResolve(accept)
  }

  return (
    <Card size="small" className="w-full" data-testid="schedule-impact-card">
      <div className="flex flex-col gap-3">
        {confirmation.widgets.length > 0 && (
          <div>
            <Typography.Text strong className="text-xs">Affected widgets</Typography.Text>
            <ul className="mt-1 space-y-1 text-xs">
              {confirmation.widgets.map((widget) => (
                <li key={widget.widgetId}>
                  {widget.title || widget.widgetId}
                  {widget.change === 'retyped' && (
                    <Typography.Text type="secondary" className="ml-1 text-xs">
                      ({typeLabel(widget.fromType)} → {typeLabel(widget.toType)})
                    </Typography.Text>
                  )}
                  {widget.change === 'removed' && (
                    <Typography.Text type="secondary" className="ml-1 text-xs">(removed)</Typography.Text>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <Typography.Text strong className="text-xs">Affected schedules</Typography.Text>
          <ul className="mt-1 space-y-1 text-xs">
            {confirmation.schedules.map((schedule) => (
              <li key={schedule.scheduleId} className="flex items-center justify-between gap-2">
                <span className="truncate">{schedule.subject || 'Untitled schedule'}</span>
                <Tag color={EFFECT_TAGS[schedule.effect]?.color} className="!mr-0 shrink-0">
                  {EFFECT_TAGS[schedule.effect]?.label ?? schedule.effect}
                </Tag>
              </li>
            ))}
          </ul>
        </div>

        {hasRetype && (
          <Alert
            type="info"
            showIcon
            className="text-xs"
            message="Changing the visual type makes this a new widget, so it is removed from schedules that picked it."
            description="To keep it in those emails, cancel and ask me to add a new widget instead."
          />
        )}

        {isReplace && (
          <Alert
            type="warning"
            showIcon
            className="text-xs"
            message="Some schedules will have no widgets left."
            description="Schedules left with no widgets are deleted and stop sending."
          />
        )}

        <div className="flex gap-2">
          {canAccept && (
            <Button
              size="small"
              type="primary"
              danger={isReplace}
              loading={resolving && action === 'accept'}
              disabled={resolving}
              onClick={() => resolve(true)}
            >
              {isReplace ? 'Apply and delete schedules' : 'Apply changes'}
            </Button>
          )}
          <Button
            size="small"
            loading={resolving && action === 'cancel'}
            disabled={resolving}
            onClick={() => resolve(false)}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  )
}
